import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ShoppingCart, Receipt, Check, Loader2, Banknote, CreditCard, Building2, Smartphone, ArrowLeft, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useBranchStore } from '@/stores/branchStore';
import { useAuthStore } from '@/stores/authStore';
import { SalesReconciliationDialog } from '@/components/sales/SalesReconciliationDialog';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import { localToday } from '@/lib/dateRange';
import { useCategorySearch } from '@/hooks/useCategorySearch';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'from-emerald-500 to-green-600' },
  { id: 'bank', label: 'Bank Transfer', icon: Building2, color: 'from-blue-500 to-indigo-600' },
  { id: 'credit', label: 'Credit', icon: CreditCard, color: 'from-amber-500 to-orange-600' },
  { id: 'online', label: 'Online', icon: Smartphone, color: 'from-violet-500 to-purple-600' },
];

const QUICK_EXPENSE_CATS = [
  { name: 'Salary', icon: '💰', color: 'bg-info/10 border-info/30 text-info' },
  { name: 'Rent', icon: '🏠', color: 'bg-violet-50 border-violet-200 text-violet-700' },
  { name: 'Utilities', icon: '⚡', color: 'bg-warning/10 border-warning/30 text-warning' },
  { name: 'Supplies', icon: '📦', color: 'bg-teal-50 border-teal-200 text-teal-700' },
  { name: 'Food & Drinks', icon: '🍔', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { name: 'Transport', icon: '🚗', color: 'bg-cyan-50 border-cyan-200 text-cyan-700' },
  { name: 'Maintenance', icon: '🔧', color: 'bg-muted/50 border-border text-foreground' },
  { name: 'Marketing', icon: '📢', color: 'bg-pink-50 border-pink-200 text-pink-700' },
  { name: 'Government', icon: '🏛️', color: 'bg-error/10 border-error/30 text-error' },
  { name: 'Cleaning', icon: '🧹', color: 'bg-lime-50 border-lime-200 text-lime-700' },
  { name: 'Internet & Phone', icon: '📱', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  { name: 'Other', icon: '📋', color: 'bg-gray-50 border-gray-200 text-gray-700' },
];

const QUICK_SALE_CATS = [
  { name: 'Product Sale', icon: '🛒', color: 'bg-success/10 border-success/30 text-success' },
  { name: 'Service', icon: '🛠️', color: 'bg-info/10 border-info/30 text-info' },
  { name: 'Food & Beverage', icon: '☕', color: 'bg-orange-50 border-orange-200 text-orange-700' },
  { name: 'Wholesale', icon: '📦', color: 'bg-violet-50 border-violet-200 text-violet-700' },
  { name: 'Delivery', icon: '🚚', color: 'bg-teal-50 border-teal-200 text-teal-700' },
  { name: 'Other', icon: '📋', color: 'bg-gray-50 border-gray-200 text-gray-700' },
];

export default function QuickEntryPage() {
  const navigate = useNavigate();
  const { branches, fetchBranches } = useBranchStore();
  const { hasPermission } = useAuthStore();
  const canReconcile = hasPermission('sales');
  const [showReconcile, setShowReconcile] = useState(false);
  const [type, setType] = useState('expense');
  const [vatInfo, setVatInfo] = useState({});
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [catQuery, setCatQuery] = useState('');
  const { match: catMatch } = useCategorySearch();
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [branchId, setBranchId] = useState('');
  const [description, setDescription] = useState('');
  // iter880 — Backdate a quick entry (e.g. an expense from last week). Defaults to today.
  const [entryDate, setEntryDate] = useState(localToday());
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [applyVat, setApplyVat] = useState(true);
  const [vatInclusive, setVatInclusive] = useState(true);
  const [vatRate, setVatRate] = useState(15);
  // Credit-specific fields
  const [customers, setCustomers] = useState([]);
  const [creditCustomerId, setCreditCustomerId] = useState('');
  const [creditInvoiceRef, setCreditInvoiceRef] = useState('');
  const [creditDueDate, setCreditDueDate] = useState('');

  // Unified tax config — auto-updates rate based on selected branch
  const { taxConfig } = useTaxConfig(branchId);
  useEffect(() => {
    if (taxConfig) {
      setApplyVat(!!taxConfig.vat_enabled);
      setVatRate(taxConfig.vat_rate || 15);
      setVatInclusive(!!taxConfig.vat_inclusive);
    }
  }, [taxConfig?.vat_enabled, taxConfig?.vat_rate, taxConfig?.vat_inclusive]);

  useEffect(() => { fetchBranches(); }, []);
  useEffect(() => {
    if (paymentMethod === 'credit' && customers.length === 0) {
      api.get('/customers').then(r => setCustomers(r.data?.data || r.data || [])).catch(() => {});
    }
  }, [paymentMethod]);

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Please enter an amount'); return; }
    if (!category) { toast.error('Please select a category'); return; }
    if (!branchId) { toast.error('Please select a branch'); return; }
    if (type === 'expense') { const _ve = vatError(vatInfo); if (_ve) { toast.error(_ve); return; } }
    if (type === 'sale' && paymentMethod === 'credit' && !creditCustomerId) {
      toast.error('Please select a customer for credit sale'); return;
    }

    setSaving(true);
    try {
      const payload = {
        amount: parseFloat(amount),
        branch_id: branchId,
        description: description || category,
        date: entryDate || localToday(),
      };

      if (type === 'expense') {
        payload.category = category;
        payload.payment_mode = paymentMethod;
        Object.assign(payload, vatPayload(vatInfo));
        await api.post('/expenses', payload);
        setVatInfo({});
        toast.success(`Expense of ${amount} recorded`);
      } else {
        payload.sale_type = paymentMethod;
        payload.payment_details = [{ mode: paymentMethod, amount: parseFloat(amount) }];
        if (paymentMethod === 'credit') {
          payload.customer_id = creditCustomerId;
          const cust = customers.find(c => c.id === creditCustomerId);
          payload.customer_name = cust?.name || category;
          if (creditInvoiceRef) payload.invoice_number_ref = creditInvoiceRef;
          if (creditDueDate) payload.due_date = creditDueDate;
        } else {
          payload.customer_name = category;
        }
        if (applyVat && vatRate > 0) {
          payload.tax_rate = vatRate;
          payload.vat_inclusive = vatInclusive;
        }
        await api.post('/sales', payload);
        toast.success(`Sale of ${amount} recorded`);
      }

      setDone(true);
      setTimeout(() => {
        setAmount(''); setCategory(''); setDescription(''); setDone(false);
        setCreditCustomerId(''); setCreditInvoiceRef(''); setCreditDueDate('');
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const cats = type === 'expense' ? QUICK_EXPENSE_CATS : QUICK_SALE_CATS;

  if (done) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4 animate-fadeIn" data-testid="quick-entry-success">
          <div className="w-20 h-20 rounded-full bg-success/20 flex items-center justify-center">
            <Check className="w-10 h-10 text-success" />
          </div>
          <p className="text-2xl font-bold text-foreground font-outfit">Saved!</p>
          <p className="text-muted-foreground">Your {type} has been recorded</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="quick-entry-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl hover:bg-muted transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-muted-foreground" />
            </button>
            <h1 className="text-2xl font-bold font-outfit tracking-tight text-foreground">Quick Entry</h1>
          </div>
          {canReconcile && (
            <Button
              variant="outline" size="sm"
              className="rounded-xl border-primary/40 text-primary hover:bg-primary/10"
              onClick={() => setShowReconcile(true)}
              data-testid="quick-entry-reconciliation-button"
            >
              <Scale className="w-4 h-4 mr-1" /> Sales Reconciliation
            </Button>
          )}
        </div>

        {/* Type Toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-2xl" data-testid="type-toggle">
          <button onClick={() => { setType('expense'); setCategory(''); }}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all ${
              type === 'expense' ? 'bg-white shadow-sm text-error' : 'text-muted-foreground hover:text-foreground'
            }`} data-testid="type-expense">
            <Receipt className="w-4 h-4" /> Expense
          </button>
          <button onClick={() => { setType('sale'); setCategory(''); }}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all ${
              type === 'sale' ? 'bg-white shadow-sm text-success' : 'text-muted-foreground hover:text-foreground'
            }`} data-testid="type-sale">
            <ShoppingCart className="w-4 h-4" /> Sale
          </button>
        </div>

        {/* Amount Input - Big and prominent */}
        <Card>
          <CardContent className="pt-6 pb-6">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">Amount *</label>
            <div className="relative">
              <Input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0.00" className="text-4xl font-bold text-center h-20 border-0 border-b-2 border-border focus:border-stone-800 rounded-none bg-transparent" 
                data-testid="amount-input" />
            </div>
            {/* VAT Section */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer" data-testid="vat-toggle">
                    <input type="checkbox" checked={applyVat} onChange={e => setApplyVat(e.target.checked)} className="sr-only peer" />
                    <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-teal-500 transition-colors"></div>
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-4 transition-transform"></div>
                  </label>
                  <span className="text-sm font-semibold text-foreground">VAT {applyVat ? 'ON' : 'OFF'}</span>
                </div>
                {applyVat && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                    {vatRate}% {vatInclusive ? 'Inclusive' : 'Exclusive'}
                  </span>
                )}
              </div>
              {applyVat && (
                <div className="grid grid-cols-2 gap-3" data-testid="vat-options">
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block">VAT Type</label>
                    <Select value={vatInclusive ? 'inclusive' : 'exclusive'} onValueChange={v => setVatInclusive(v === 'inclusive')}>
                      <SelectTrigger className="h-9 text-xs" data-testid="vat-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inclusive">Inclusive (amount includes VAT)</SelectItem>
                        <SelectItem value="exclusive">Exclusive (VAT added on top)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1 block">VAT Rate %</label>
                    <Input type="number" value={vatRate} onChange={e => setVatRate(parseFloat(e.target.value) || 0)}
                      className="h-9 text-xs" data-testid="vat-rate-input" />
                  </div>
                  {amount && parseFloat(amount) > 0 && (
                    <div className="col-span-2 bg-teal-50 rounded-lg p-2.5 text-xs" data-testid="vat-preview">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Net Amount:</span>
                        <span className="font-semibold">{vatInclusive ? (parseFloat(amount) / (1 + vatRate / 100)).toFixed(2) : parseFloat(amount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-teal-700 font-medium mt-1">
                        <span>VAT ({vatRate}%):</span>
                        <span>{vatInclusive ? (parseFloat(amount) - parseFloat(amount) / (1 + vatRate / 100)).toFixed(2) : (parseFloat(amount) * vatRate / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-foreground font-bold mt-1 pt-1 border-t border-teal-200">
                        <span>Total:</span>
                        <span>{vatInclusive ? parseFloat(amount).toFixed(2) : (parseFloat(amount) * (1 + vatRate / 100)).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Category Grid */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">
            {type === 'expense' ? 'What was it for?' : 'Sale type'} *
          </label>
          {type === 'expense' && (
            <Input value={catQuery} onChange={e => setCatQuery(e.target.value)} placeholder="Search category, account or code…"
              className="h-8 text-xs mb-2" data-testid="quick-category-search" />
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="category-grid">
            {cats.filter(cat => type !== 'expense' || !catQuery || category === cat.name || catMatch(cat.name, catQuery)).map(cat => (
              <button key={cat.name} onClick={() => setCategory(cat.name)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                  category === cat.name ? 'border-stone-800 bg-muted/50 shadow-sm scale-[1.02]' : `border-transparent ${cat.color}`
                }`} data-testid={`cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <span className="text-xl">{cat.icon}</span>
                <span className="text-[11px] font-semibold leading-tight">{cat.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 block">Payment Method</label>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="payment-methods">
            {PAYMENT_METHODS.map(pm => {
              const Icon = pm.icon;
              return (
                <button key={pm.id} onClick={() => setPaymentMethod(pm.id)}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all ${
                    paymentMethod === pm.id ? 'bg-muted text-white shadow-md' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`} data-testid={`pay-${pm.id}`}>
                  <Icon className="w-5 h-5" />
                  <span className="text-[10px] font-semibold">{pm.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Credit-specific fields */}
        {type === 'sale' && paymentMethod === 'credit' && (
          <div className="p-4 bg-warning/10 border-2 border-warning/30 rounded-xl space-y-3" data-testid="credit-fields">
            <div className="flex items-center gap-2 text-warning">
              <CreditCard size={16} />
              <span className="text-sm font-semibold">Credit Sale Details</span>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Customer *</label>
              <SearchableSelect
                items={customers.map(c => ({
                  id: c.id,
                  name: `${c.name}${c.ref ? ` (${c.ref})` : ''}${c.phone ? ` • ${c.phone}` : ''}`,
                }))}
                value={creditCustomerId}
                onChange={setCreditCustomerId}
                placeholder="Search customer by name / ref / phone…"
                data-testid="credit-customer-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Invoice / Ref # (optional)</label>
                <Input
                  value={creditInvoiceRef}
                  onChange={(e) => setCreditInvoiceRef(e.target.value)}
                  placeholder="e.g., INV-0042"
                  data-testid="credit-invoice-ref"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Due Date (optional)</label>
                <Input
                  type="date"
                  value={creditDueDate}
                  onChange={(e) => setCreditDueDate(e.target.value)}
                  data-testid="credit-due-date"
                />
              </div>
            </div>
          </div>
        )}

        {/* Branch & Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">Branch *</label>
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            {/* iter880 — Entry date selector for backdating. */}
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">Entry date</label>
            <Input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} data-testid="entry-date-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">Notes (optional)</label>
            <Input value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Add a note..." data-testid="notes-input" />
          </div>
          {type === 'expense' && (
            <VatTreatmentFields value={vatInfo} amount={amount} rate={vatRate || 15} onChange={(p) => setVatInfo((v) => ({ ...v, ...p }))} testPrefix="quick-vat" />
          )}
        </div>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving || !amount || !category || !branchId}
          className={`w-full py-6 text-lg font-bold rounded-2xl transition-all ${
            type === 'expense' ? 'bg-error hover:bg-error' : 'bg-success hover:bg-success'
          }`} data-testid="save-btn">
          {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Saving...</>
            : `Record ${type === 'expense' ? 'Expense' : 'Sale'}`}
        </Button>

        <SalesReconciliationDialog
          open={showReconcile}
          onOpenChange={setShowReconcile}
          branches={branches}
          onPosted={() => {}}
        />
      </div>
    </DashboardLayout>
  );
}
